/** Pure helpers for dine-in table assignment and overlap checks (stores + dashboard). */
import moment from "moment-timezone";
import { FORMATS } from "../core/formats";
import { isValidJoinCombo, } from "./table_join";
import { buildBlockedByTableFromBlocks, filterTableBlocksForDay, mergeOccupiedByTableMaps, } from "./table_blocks";
export { hasFloorPlanLayouts, isConnectedJoinSet, isValidJoinCombo, rectGap, tablesAreJoinable, JOIN_ADJACENT_GAP_MAX, } from "./table_join";
export { blockToIntervalMs, buildBlockedByTableFromBlocks, filterTableBlocksForDay, mergeOccupiedByTableMaps, tableBlockedForWindow, } from "./table_blocks";
export { ALL_TABLES_AREA_ID, ALL_TABLES_AREA_LABEL, DEFAULT_MAIN_FLOOR_ID, DEFAULT_MAIN_FLOOR_NAME, ensureFloorPlans, filterTablesByFloor, groupTablesByArea, resolveTableArea, resolveTableFloorId, UNASSIGNED_AREA_ID, UNASSIGNED_AREA_LABEL, } from "./floor_plans";
export { bookingUsesTable, FLOOR_STATUS_COLORS, orderUsesTable, resolveFloorTableLiveState, } from "./floor_live";
export { applyTableTemplate, clearTableTemplateOverride, resolveTableTemplate, resolveTableTemplateId, TABLE_TEMPLATES, templateIdFromTable, } from "./table_templates";
export { bookingTimestampToDateStr, isBookingDatePaused, normalizeBookingPausedDates, } from "./booking_paused_dates";
/** Joined table names for docket / floor UI (e.g. `T1 + T2`). */
export function formatBookingTableLabel(tableIds, tables) {
    if (!tableIds?.length)
        return null;
    return tableIds
        .map((id) => {
        const name = tables.find((t) => t._id === id)?.name?.trim();
        return name || "Table";
    })
        .join(" + ");
}
/** Physical printer layout for Live table docket (dashboard modal). */
export function isLiveTableDocketBooking(b) {
    return b.status === "seated";
}
/** Headline on printed/browser live docket (e.g. `TABLE 5` or `TABLE T1 + T2`). */
export function liveTableDocketTableHeadline(tableIds, tables) {
    const tableId = tableIds?.[0] || null;
    if (!tableId)
        return "Unassigned";
    const joinedLabel = formatBookingTableLabel(tableIds, tables);
    const table = tables.find((x) => x._id === tableId);
    const tableName = joinedLabel || table?.name?.trim() || "Table";
    return `TABLE ${tableName}`;
}
function intervalsOverlap(a, b) {
    return a.start < b.end && b.start < a.end;
}
export function dineInTableCapacity(t, fallback = 4) {
    return t.seats && t.seats > 0 ? t.seats : fallback;
}
/**
 * Largest party the floor plan can ever seat in one assignment (single table or
 * valid 2–3 table join). Does not consider existing bookings — use with service max.
 */
export function maxBookablePartyForTableLayout(tables, defaultSeatFallback = 4) {
    if (!tables.length)
        return 0;
    let max = 0;
    for (const t of tables) {
        max = Math.max(max, dineInTableCapacity(t, defaultSeatFallback));
    }
    for (let i = 0; i < tables.length; i++) {
        for (let j = i + 1; j < tables.length; j++) {
            const ids = [tables[i]._id, tables[j]._id];
            if (!isValidJoinCombo(ids, tables))
                continue;
            const cap = dineInTableCapacity(tables[i], defaultSeatFallback) +
                dineInTableCapacity(tables[j], defaultSeatFallback);
            max = Math.max(max, cap);
        }
    }
    for (let i = 0; i < tables.length; i++) {
        for (let j = i + 1; j < tables.length; j++) {
            for (let k = j + 1; k < tables.length; k++) {
                const ids = [tables[i]._id, tables[j]._id, tables[k]._id];
                if (!isValidJoinCombo(ids, tables))
                    continue;
                const cap = dineInTableCapacity(tables[i], defaultSeatFallback) +
                    dineInTableCapacity(tables[j], defaultSeatFallback) +
                    dineInTableCapacity(tables[k], defaultSeatFallback);
                max = Math.max(max, cap);
            }
        }
    }
    return max;
}
/** Guest/staff stepper bounds: service min/max capped by largest joinable table set (max 3 tables). */
export function resolveTableBookingPartyLimits(args) {
    const min = args.min_people && args.min_people > 0 ? Number(args.min_people) : 1;
    let configuredMax = args.max_people || args.limit_number_of_people;
    if ((!configuredMax || configuredMax <= 0) && args.fallbackMaxWhenUnset) {
        configuredMax = args.fallbackMaxWhenUnset;
    }
    const layoutMax = args.capMaxByTableLayout !== false &&
        args.tables &&
        args.tables.length
        ? maxBookablePartyForTableLayout(args.tables)
        : 0;
    let max = configuredMax && configuredMax > 0 ? Number(configuredMax) : undefined;
    if (layoutMax > 0) {
        max = max !== undefined ? Math.min(max, layoutMax) : layoutMax;
    }
    return { min, max };
}
/**
 * Bookings that occupy physical tables for overlap purposes.
 */
function bookingOccupiesTables(b) {
    if (b.status === "cancelled" || b.status === "complete")
        return false;
    const ids = b.table_ids;
    return !!(ids && ids.length);
}
function turnMs(b, defaultTurnMinutes) {
    const m = b.table_turn_minutes && b.table_turn_minutes > 0 ? b.table_turn_minutes : defaultTurnMinutes;
    return m * 60_000;
}
export function buildOccupiedByTableFromBookings(bookings, defaultTurnMinutes, excludeBookingId) {
    const map = new Map();
    for (const b of bookings) {
        if (excludeBookingId && b._id === excludeBookingId)
            continue;
        if (!bookingOccupiesTables(b))
            continue;
        const iv = {
            start: b.config.timestamp,
            end: b.config.timestamp + turnMs(b, defaultTurnMinutes),
        };
        for (const tid of b.table_ids) {
            if (!map.has(tid))
                map.set(tid, []);
            map.get(tid).push(iv);
        }
    }
    return map;
}
/** Bookings + table blocks for assignment / overlap checks on a given day. */
export function buildOccupiedByTableForAssignment(bookings, blocks, defaultTurnMinutes, tz, dayDateStr, excludeBookingId) {
    const fromBookings = buildOccupiedByTableFromBookings(bookings, defaultTurnMinutes, excludeBookingId);
    const dayBlocks = filterTableBlocksForDay(blocks, dayDateStr);
    const fromBlocks = buildBlockedByTableFromBlocks(dayBlocks, tz);
    return mergeOccupiedByTableMaps(fromBookings, fromBlocks);
}
function tableFreeForWindow(tableId, window, occupied) {
    const list = occupied.get(tableId);
    if (!list || !list.length)
        return true;
    return !list.some((iv) => intervalsOverlap(iv, window));
}
/** Tables that already have an overlapping reservation for this time window. */
export function countOccupiedTablesAtWindow(tables, window, occupied) {
    let n = 0;
    for (const t of tables) {
        if (!tableFreeForWindow(t._id, window, occupied))
            n++;
    }
    return n;
}
function comboCapacity(ids, tables, fallback) {
    let cap = 0;
    for (const id of ids) {
        const t = tables.find((x) => x._id === id);
        if (!t)
            return 0;
        cap += dineInTableCapacity(t, fallback);
    }
    return cap;
}
/** Prefer fewer tables, then least excess capacity above party size. */
function pickBestJoinOption(options, party, tables, fallback) {
    if (!options.length)
        return null;
    let best = options[0];
    let bestCount = best.table_ids.length;
    let bestExcess = comboCapacity(best.table_ids, tables, fallback) - party;
    for (let i = 1; i < options.length; i++) {
        const o = options[i];
        const count = o.table_ids.length;
        const excess = comboCapacity(o.table_ids, tables, fallback) - party;
        if (count < bestCount || (count === bestCount && excess < bestExcess)) {
            best = o;
            bestCount = count;
            bestExcess = excess;
        }
    }
    return best.table_ids;
}
export function tryAssignTablesForParty(tables, party, window, occupied, defaultSeatFallback = 4) {
    const singles = tables
        .map((t) => ({ t, c: dineInTableCapacity(t, defaultSeatFallback) }))
        .filter(({ c }) => c >= party)
        .sort((a, b) => a.c - b.c);
    for (const { t } of singles) {
        if (tableFreeForWindow(t._id, window, occupied))
            return [t._id];
    }
    const multiOptions = listMoveTableOptions(tables, party, window, occupied, defaultSeatFallback, false).filter((o) => o.table_ids.length > 1);
    return pickBestJoinOption(multiOptions, party, tables, defaultSeatFallback);
}
/** Guest-facing copy when a chosen slot cannot seat the party. */
export const BOOKING_SLOT_UNAVAILABLE_MESSAGE = "We're fully booked for this party size. Please try another slot.";
/**
 * Time values (HH:mm) that cannot seat `party` on `date`.
 * Uses the same single-table then join-combo rules as assignment.
 * Restaurants with no tables are left unrestricted.
 */
export function unavailableBookingTimeValues(args) {
    const { tables, bookings, blocks, party, date, times, tz, turnMinutes } = args;
    if (!tables.length || !times.length || party <= 0)
        return [];
    const turn = turnMinutes > 0 ? turnMinutes : 90;
    const occupied = buildOccupiedByTableForAssignment(bookings, blocks, turn, tz, date);
    const unavailable = [];
    for (const time of times) {
        if (!time)
            continue;
        const start = moment.tz(`${date} ${time}`, FORMATS.moment.datetime, tz).valueOf();
        if (!Number.isFinite(start))
            continue;
        const window = { start, end: start + turn * 60_000 };
        if (!tryAssignTablesForParty(tables, party, window, occupied)) {
            unavailable.push(time);
        }
    }
    return unavailable;
}
export function validateExplicitTableAssignment(table_ids, tables, party, window, occupied, defaultSeatFallback = 4, opts) {
    if (!table_ids.length)
        return false;
    if (!opts?.keepExistingCombo && !isValidJoinCombo(table_ids, tables))
        return false;
    const idSet = new Set(tables.map((t) => t._id));
    for (const id of table_ids) {
        if (!idSet.has(id))
            return false;
    }
    let cap = 0;
    for (const id of table_ids) {
        const t = tables.find((x) => x._id === id);
        if (!t)
            return false;
        if (!tableFreeForWindow(id, window, occupied))
            return false;
        cap += dineInTableCapacity(t, defaultSeatFallback);
    }
    return cap >= party;
}
export function listMoveTableOptions(tables, party, window, occupied, defaultSeatFallback = 4, 
/** When true, only single-table options (no 2- or 3-table combinations). */
standaloneOnly = false) {
    const out = [];
    const seen = new Set();
    const push = (ids) => {
        if (!isValidJoinCombo(ids, tables))
            return;
        const key = [...ids].sort().join(",");
        if (seen.has(key))
            return;
        seen.add(key);
        const labels = ids.map((id) => {
            const t = tables.find((x) => x._id === id);
            return (t?.name && t.name.trim()) || id;
        });
        out.push({ table_ids: ids, label: labels.join(" + ") });
    };
    for (const t of tables) {
        const c = dineInTableCapacity(t, defaultSeatFallback);
        if (c >= party && tableFreeForWindow(t._id, window, occupied))
            push([t._id]);
    }
    if (standaloneOnly) {
        return out;
    }
    for (let i = 0; i < tables.length; i++) {
        for (let j = i + 1; j < tables.length; j++) {
            const a = tables[i];
            const b = tables[j];
            const ca = dineInTableCapacity(a, defaultSeatFallback);
            const cb = dineInTableCapacity(b, defaultSeatFallback);
            if (ca + cb < party)
                continue;
            if (!tableFreeForWindow(a._id, window, occupied))
                continue;
            if (!tableFreeForWindow(b._id, window, occupied))
                continue;
            push([a._id, b._id]);
        }
    }
    for (let i = 0; i < tables.length; i++) {
        for (let j = i + 1; j < tables.length; j++) {
            for (let k = j + 1; k < tables.length; k++) {
                const trip = [tables[i], tables[j], tables[k]];
                const sum = trip.reduce((s, t) => s + dineInTableCapacity(t, defaultSeatFallback), 0);
                if (sum < party)
                    continue;
                if (!trip.every((t) => tableFreeForWindow(t._id, window, occupied)))
                    continue;
                push(trip.map((t) => t._id));
            }
        }
    }
    return out;
}
//# sourceMappingURL=index.js.map